/**
 * Smart Inventory Sort & Filter — Collection Product Grid
 *
 * 1. SORT
 *    Default: highest total stock first.
 *    With variant-option filters: high/low interleave using
 *    matchingStock + totalStock for sell-through.
 *
 * 2. HIDE OUT-OF-STOCK VARIANTS
 *    When a filter is active, products whose matching variants have 0 stock
 *    are hidden by default. A toggle button lets the user show them anyway.
 *
 *    data-variants-stock on each grid item provides per-variant inventory:
 *    [{"o":["38","Blau"],"q":5}, {"o":["40","Blau"],"q":0}, ...]
 */
(function () {
  'use strict';

  const GRID_SEL = '#product-grid';
  const ITEM_SEL = '.grid__item[data-stock]';
  const TOGGLE_ID = 'jm-show-oos-toggle';
  const HIDDEN_CLASS = 'jm-oos-hidden';
  const BADGE_CLASS = 'jm-oos-badge';

  let showOOS = false; // user preference within session

  /* ── helpers ─────────────────────────────────────────────── */

  function getStock(el) {
    return parseInt(el.dataset.stock, 10) || 0;
  }

  function normalizeFilterValue(value) {
    return decodeURIComponent(String(value || '')).trim().toLowerCase();
  }

  function getPositiveStock(value) {
    return Math.max(parseInt(value, 10) || 0, 0);
  }

  function isVariantOptionFilterKey(key) {
    return key.toLowerCase().startsWith('filter.v.option.');
  }

  function getVariantOptionFilterGroups() {
    const params = new URLSearchParams(window.location.search);
    const groups = [];
    const groupIndexByKey = {};
    for (const [key, val] of params) {
      const normalizedKey = key.toLowerCase();
      if (!isVariantOptionFilterKey(normalizedKey) || !val) continue;
      if (groupIndexByKey[normalizedKey] == null) {
        groupIndexByKey[normalizedKey] = groups.length;
        groups.push([]);
      }
      var group = groups[groupIndexByKey[normalizedKey]];
      val.split(',').forEach(function (part) {
        var normalized = normalizeFilterValue(part);
        if (normalized && group.indexOf(normalized) === -1) {
          group.push(normalized);
        }
      });
    }
    return groups.filter(function (group) {
      return group.length > 0;
    });
  }

  function hasAnyFilter() {
    /* Only treat USER-selected filters as "active" — ignore the auto-injected
     * filter.v.availability=1 from the redirect script at the top of
     * sections/main-collection-product.liquid. That param is server-side
     * filtering (Shopify already excludes unavailable products), not a
     * user variant filter. Treating it as active caused this JS to
     * hide every untracked-inventory product (data-stock="0") even
     * when the product was actually purchasable.
     *
     * Bugfix 2026-04: "alle artikel als ausverkauft markiert" — products
     * without inventory_management on any variant get _total_qty=0 in
     * main-collection-product.liquid (correct), and this JS misinterpreted
     * data-stock="0" + auto-availability filter as "all filtered out". */
    const params = new URLSearchParams(window.location.search);
    for (const [key] of params) {
      const k = key.toLowerCase();
      if (!k.startsWith('filter.')) continue;
      if (k === 'filter.v.availability') continue;
      return true;
    }
    return false;
  }

  function getCurrentSortBy() {
    const params = new URLSearchParams(window.location.search);
    return params.get('sort_by') || '';
  }

  function shouldApplyStockSort() {
    const currentSortBy = getCurrentSortBy();
    return currentSortBy === '' || currentSortBy === 'best-selling';
  }

  /**
   * Read the centralized variant-stock map emitted by
   * sections/main-collection-product.liquid as <script id="jm-grid-stock-map">.
   * Replaces the legacy per-card data-variants-stock attribute. Cached per
   * processGrid() invocation since the map is stable for the rendered DOM.
   * Format: { "<product-handle>": [{"o":["38","Blau"],"q":5}, ...], ... }
   */
  function readStockMap() {
    // Scoped to the grid container to support multiple section instances on
    // one page; matches the section-ID-suffixed <script class="jm-grid-stock-map">
    // emitted by sections/main-collection-product.liquid.
    var grid = document.querySelector(GRID_SEL);
    var node = grid && grid.parentElement
      ? grid.parentElement.querySelector('.jm-grid-stock-map')
      : document.querySelector('.jm-grid-stock-map');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Sum the stock of all variants that match the active variant-option filters.
   * Within one filter key, selected values behave as OR.
   * Across different filter keys, filters behave as AND.
   *
   * Returns null when per-variant stock data is unavailable on the element
   * (handle not in the centralized stock map, e.g. for untracked-inventory
   * products). Callers MUST treat null as "unknown" — never use it as 0,
   * otherwise untracked-inventory products get hidden as OOS.
   */
  function getMatchingStock(el, filterGroups, stockMap) {
    var handle = el.dataset.productHandle;
    var variants = handle && Object.prototype.hasOwnProperty.call(stockMap, handle)
      ? stockMap[handle]
      : null;
    // Fallback for AJAX-appended cards (theme.js loadmore extracts only
    // .loadmore-item nodes, the central <script class="jm-grid-stock-map">
    // does not reach Page 2+). Only enter the try/parse path when the legacy
    // attribute is actually present — avoids a per-card JSON.parse('[]') for
    // untracked products.
    if (!variants && el.dataset.variantsStock) {
      try {
        variants = JSON.parse(el.dataset.variantsStock);
      } catch (e) {
        return null;
      }
    }
    if (!variants || !variants.length) return null;
    if (!filterGroups.length) return getStock(el);

    return variants.reduce(function (total, v) {
      var variantStock = getPositiveStock(v.q);
      if (variantStock <= 0) return total;
      const opts = v.o.map(function (o) { return o.toLowerCase(); });
      var matches = filterGroups.every(function (group) {
        return group.some(function (filterValue) {
          return opts.some(function (o) {
            return o === filterValue;
          });
        });
      });
      return matches ? total + variantStock : total;
    }, 0);
  }

  /* ── interleave ──────────────────────────────────────────── */
  function interleaveRankings(highSorted, lowSorted) {
    const result = [];
    const usedElements = new Set();
    let highIndex = 0;
    let lowIndex = 0;
    let pickHigh = true;

    function takeNext(sorted, startIndex) {
      let index = startIndex;
      while (index < sorted.length) {
        const candidate = sorted[index];
        if (!usedElements.has(candidate.el)) {
          usedElements.add(candidate.el);
          result.push(candidate.el);
          return index + 1;
        }
        index += 1;
      }
      return index;
    }

    while (result.length < highSorted.length) {
      const previousLength = result.length;
      if (pickHigh) {
        highIndex = takeNext(highSorted, highIndex);
      } else {
        lowIndex = takeNext(lowSorted, lowIndex);
      }
      if (result.length === previousLength) {
        if (pickHigh) {
          lowIndex = takeNext(lowSorted, lowIndex);
        } else {
          highIndex = takeNext(highSorted, highIndex);
        }
      }
      if (result.length === previousLength) break;
      pickHigh = !pickHigh;
    }

    return result;
  }

  /* ── toggle button ───────────────────────────────────────── */

  function ensureToggle(grid, hiddenCount) {
    let wrap = grid.parentElement.querySelector('#' + TOGGLE_ID);
    if (hiddenCount === 0) {
      if (wrap) wrap.remove();
      return;
    }
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.id = TOGGLE_ID;
      wrap.style.cssText = 'text-align:center;padding:16px 0 8px;';
      wrap.innerHTML =
        '<button type="button" class="jm-oos-toggle-btn" style="' +
        'display:inline-flex;align-items:center;gap:8px;padding:8px 20px;' +
        'border:1px solid #ccc;border-radius:24px;background:#fff;' +
        'font-size:13px;font-family:inherit;cursor:pointer;color:#555;' +
        'transition:all .2s ease">' +
        '<span class="jm-oos-toggle-label"></span>' +
        '</button>';
      grid.parentElement.insertBefore(wrap, grid.nextSibling);
      wrap.querySelector('button').addEventListener('click', function () {
        showOOS = !showOOS;
        processGrid();
      });
    }
    var label = wrap.querySelector('.jm-oos-toggle-label');
    if (showOOS) {
      label.textContent = 'Ausverkaufte ausblenden (' + hiddenCount + ')';
      wrap.querySelector('button').style.borderColor = '#1c2a3a';
      wrap.querySelector('button').style.color = '#1c2a3a';
    } else {
      label.textContent = 'Auch ausverkaufte anzeigen (' + hiddenCount + ')';
      wrap.querySelector('button').style.borderColor = '#ccc';
      wrap.querySelector('button').style.color = '#555';
    }
  }

  /* ── main logic ──────────────────────────────────────────── */

  function processGrid() {
    const grid = document.querySelector(GRID_SEL);
    if (!grid) return;

    function unmask() {
      requestAnimationFrame(function () {
        grid.classList.remove('jm-sort-mask');
      });
    }

    const items = Array.from(grid.querySelectorAll(ITEM_SEL));
    if (!items.length) {
      unmask();
      return;
    }

    // hasAnyFilter() is no longer used directly — OOS-hiding is gated on
    // variantFiltersActive only (see show/hide loop). Kept for clarity if
    // future logic needs to distinguish "any filter" vs "variant filter".
    const variantFilterGroups = getVariantOptionFilterGroups();
    const variantFiltersActive = variantFilterGroups.length > 0;
    const stockContextCache = new Map();
    const stockMap = readStockMap();
    let hiddenCount = 0;
    var includeTotalHigh = grid.dataset.sortHighIncludeTotal === 'true';
    var includeTotalLow = grid.dataset.sortLowIncludeTotal === 'true';

    function getStockContext(el) {
      if (stockContextCache.has(el)) return stockContextCache.get(el);
      var totalStock = getStock(el);
      var matchingStockRaw = variantFiltersActive
        ? getMatchingStock(el, variantFilterGroups, stockMap)
        : totalStock;
      // matchingStockRaw can be null (unknown — no per-variant data on el).
      // For sort math, fall back to totalStock so products without variant
      // data don't sort as 0. For OOS-hiding, callers must check raw==null
      // separately and skip hiding.
      var matchingStock = matchingStockRaw == null ? totalStock : matchingStockRaw;
      var highScore = variantFiltersActive
        ? matchingStock + (includeTotalHigh ? totalStock : 0)
        : totalStock;
      var lowScore = variantFiltersActive
        ? matchingStock + (includeTotalLow ? totalStock : 0)
        : totalStock;
      var context = {
        el: el,
        totalStock: totalStock,
        matchingStock: matchingStock,
        matchingStockKnown: matchingStockRaw != null,
        highScore: highScore,
        lowScore: lowScore
      };
      stockContextCache.set(el, context);
      return context;
    }

    // 1. Show/hide based on variant stock
    items.forEach(function (el) {
      el.classList.remove(HIDDEN_CLASS);
      // Remove any existing OOS badge
      var badge = el.querySelector('.' + BADGE_CLASS);
      if (badge) badge.remove();

      var context = getStockContext(el);
      // OOS-hiding ONLY makes sense when a variant-option filter is active
      // (e.g. ?filter.v.option.color=blue). For non-variant filters (brand,
      // tag, vendor, price, availability), hiding by total stock would
      // wrongly hide untracked products. Skip if no variant filter is active.
      if (!variantFiltersActive) return;
      // Skip if we don't have per-variant data on this element — we can't
      // tell which variant the user is filtering for (untracked-inventory
      // products and non-discontinued products fall here under the current
      // Liquid emission policy).
      if (!context.matchingStockKnown) return;
      if (context.matchingStock <= 0) {
        hiddenCount++;
        if (!showOOS) {
          el.classList.add(HIDDEN_CLASS);
        } else {
          // Show but mark as out-of-stock
          var b = document.createElement('div');
          b.className = BADGE_CLASS;
          b.textContent = 'Ausverkauft';
          b.style.cssText =
            'position:absolute;top:8px;left:8px;z-index:5;' +
            'background:rgba(0,0,0,.65);color:#fff;font-size:11px;' +
            'padding:3px 10px;border-radius:12px;pointer-events:none;';
          el.style.position = 'relative';
          el.insertBefore(b, el.firstChild);
        }
      }
    });

    // Show toggle only when filters are active and there are hidden items
    ensureToggle(grid, hiddenCount);

    // 2. Sort visible items
    const visible = items.filter(function (el) {
      return !el.classList.contains(HIDDEN_CLASS);
    });

    if (visible.length < 2 || !shouldApplyStockSort()) {
      unmask();
      return;
    }

    const byLowScoreAsc = visible.slice().sort(function (a, b) {
      var contextA = getStockContext(a);
      var contextB = getStockContext(b);
      if (contextA.lowScore !== contextB.lowScore) {
        return contextA.lowScore - contextB.lowScore;
      }
      if (contextA.matchingStock !== contextB.matchingStock) {
        return contextA.matchingStock - contextB.matchingStock;
      }
      return contextA.totalStock - contextB.totalStock;
    });

    const byHighScoreDesc = visible.slice().sort(function (a, b) {
      var contextA = getStockContext(a);
      var contextB = getStockContext(b);
      if (contextA.highScore !== contextB.highScore) {
        return contextB.highScore - contextA.highScore;
      }
      if (contextA.matchingStock !== contextB.matchingStock) {
        return contextB.matchingStock - contextA.matchingStock;
      }
      return contextB.totalStock - contextA.totalStock;
    });

    var ordered;
    if (variantFiltersActive) {
      ordered = interleaveRankings(
        byHighScoreDesc.map(getStockContext),
        byLowScoreAsc.map(getStockContext)
      );
    } else {
      ordered = byHighScoreDesc;
    }

    /* Use DocumentFragment to prevent O(N) DOM thrashing layout calculations */
    const fragment = document.createDocumentFragment();
    ordered.forEach(function (el) { fragment.appendChild(el); });
    grid.appendChild(fragment);

    /* Unmask grid after sorting is complete to prevent flash */
    unmask();
  }

  function scheduleProcessGrid() {
    /* Use idle callback so sorting doesn't block input/paint.
     * Timeout ensures it runs within 800ms even if main thread is busy. */
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () { processGrid(); }, { timeout: 800 });
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(processGrid);
      });
    }
  }

  /* ── CSS injection ───────────────────────────────────────── */

  var style = document.createElement('style');
  style.textContent =
    '.' + HIDDEN_CLASS + '{display:none!important}' +
    '.jm-oos-toggle-btn:hover{background:#f5f5f5!important}';
  document.head.appendChild(style);

  /* ── lifecycle ───────────────────────────────────────────── */

  window.jmProcessCollectionGrid = scheduleProcessGrid;

  // Execute immediately to prevent FOUC (assumes script is defer or at body end)
  scheduleProcessGrid();

  document.addEventListener('shopify:section:load', scheduleProcessGrid);
  document.addEventListener('theme:dom-updated', function (event) {
    var container = event.detail && event.detail.container;
    if (container && (container.matches?.(GRID_SEL) || container.querySelector?.(GRID_SEL) || container.id === 'productgridcontainer')) {
      scheduleProcessGrid();
    }
  });
  window.addEventListener('pageshow', scheduleProcessGrid);
})();
